import internetIdentity from '@assets/icons/internet-identity.svg'
import oisy from '@assets/icons/oisy.svg'
import plug from '@assets/icons/plug-logo.png'
import {
  clearKeychain,
  createWallet as createWalletHandler,
} from '@plug-core/handlers/walletActions/wallet'
import { GAEventName } from '@plug-core/interfaces/googleAnalytics'
import { logger } from '@plug-core/logger'
import { useGoogleAnalytics } from '@plug-core/providers/GoogleAnalyticsProvider'
import { useConnectionProvider, useCurrentWallet } from '@plug-core/state'
import { ConnectionProvider } from '@plug-core/state/connectionProvider'
import { addDefaultWalletTokens } from '@plug-core/utils/addDefaultTokens'
import { setDefaultWalletName } from '@plug-core/utils/setDefaultWalletName'
import { useFindTokensProvider } from '@providers/FindTokensProvider'
import { connectII } from '@utils/internetIdentity'
import { connectOisy } from '@utils/oisy'
import { connectPlug } from '@utils/plugExtension'
import { ROUTES } from '@utils/routes'
import { WALLET_SETUP_ROUTES } from '@utils/walletSetupRoutes'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

export const authMethods = {
  plug: {
    title: 'Plug Wallet',
    icon: plug,
    connect: connectPlug,
  },
  'internet-identity-legacy': {
    title: 'Internet Identity - Legacy',
    icon: internetIdentity,
    connect: () => connectII('legacy'),
  },
  'internet-identity-v2': {
    title: 'Internet Identity',
    icon: internetIdentity,
    connect: () => connectII('v2'),
  },
  oisy: {
    title: 'Oisy Wallet',
    icon: oisy,
    connect: connectOisy,
  },
}

export const useConnect = () => {
  const [connectingProvider, setConnectingProvider] = useState<ConnectionProvider>(null)
  const [error, setError] = useState('')

  const { setCurrentWallet, currentWallet } = useCurrentWallet()
  const { setProvider } = useConnectionProvider()
  const { findTokens } = useFindTokensProvider()
  const { fireEvent } = useGoogleAnalytics()
  const navigate = useNavigate()

  const onConnect = async (provider: ConnectionProvider) => {
    await clearKeychain()

    setConnectingProvider(provider)

    try {
      const { principal } = await authMethods[provider!].connect()

      findTokens(principal).catch(logger.debug)

      setCurrentWallet(principal)
      await setDefaultWalletName(principal)

      setProvider(provider)

      fireEvent(GAEventName.SIGN_IN, { sign_in_method: provider })

      if (provider === 'internet-identity-legacy' || provider === 'internet-identity-v2') {
        navigate(WALLET_SETUP_ROUTES.PASSWORD)
      } else {
        navigate(ROUTES.ROOT)
      }
    } catch (e: any) {
      setError(e.toString())
      logger.error(e)
    } finally {
      setConnectingProvider(null)
    }
  }

  const createWallet = async () => {
    await clearKeychain()

    const { mnemonic, principal } = await createWalletHandler()

    setCurrentWallet(principal)
    addDefaultWalletTokens(principal)

    await setDefaultWalletName(principal)

    fireEvent(GAEventName.SIGN_IN, { sign_in_method: 'new-wallet' })
    fireEvent(GAEventName.WALLET_CREATED, { method: 'new-wallet' })

    navigate(WALLET_SETUP_ROUTES.BACKUP_SEED, { state: { mnemonic } })
  }

  return { onConnect, error, connectingProvider, createWallet }
}
